// @ts-nocheck
import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Upload, Download, AlertCircle, Check, X, Users } from 'lucide-react';
import { sendRosterInvitation } from '@/utils/rosterInvitations';
import { useAuth } from '@/contexts/AuthContext';
import { getSessionUser } from '@/lib/authSession';

interface CSVRow {
  first_name: string;
  last_name: string;
  email: string;
  cell_phone: string;
  isValid: boolean;
  errors: string[];
  existingProfile?: string;
}

interface RosterCSVImportProps {
  teamId: string;
  teamName: string;
  onImportComplete: () => void;
}

const RosterCSVImport: React.FC<RosterCSVImportProps> = ({ teamId, teamName, onImportComplete }) => {
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const generateTemplate = () => {
    const headers = ['first_name', 'last_name', 'email', 'cell_phone'];
    const sampleData = [
      ['John', 'Smith', 'john.smith@email.com', '555-123-4567'],
      ['Jane', 'Doe', 'jane.doe@email.com', '555-987-6543'],
      ['Mike', 'Johnson', 'mike.johnson@email.com', '555-456-7890']
    ];

    const csvContent = [headers, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `roster-template-${teamName.replace(/[^a-zA-Z0-9]/g, '_')}.csv`;
    link.click();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true; // Phone is optional
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's 10 or 11 digits (US phone numbers)
    return digitsOnly.length === 10 || digitsOnly.length === 11;
  };

  const parseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      toast.error('CSV file must have at least a header row and one data row');
      return [];
    }

    const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
    
    // Validate headers
    const requiredHeaders = ['first_name', 'last_name', 'email'];
    const optionalHeaders = ['cell_phone'];
    const allValidHeaders = [...requiredHeaders, ...optionalHeaders];
    
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    if (missingHeaders.length > 0) {
      toast.error(`Missing required headers: ${missingHeaders.join(', ')}`);
      return [];
    }

    const invalidHeaders = headers.filter(h => h && !allValidHeaders.includes(h));
    if (invalidHeaders.length > 0) {
      toast.warning(`Unknown headers will be ignored: ${invalidHeaders.join(', ')}`);
    }

    const data: CSVRow[] = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.replace(/"/g, '').trim());
      const row: CSVRow = {
        first_name: '',
        last_name: '',
        email: '',
        cell_phone: '',
        isValid: true,
        errors: []
      };

      // Map values to fields based on header positions
      headers.forEach((header, index) => {
        if (allValidHeaders.includes(header) && values[index]) {
          (row as any)[header] = values[index];
        }
      });

      // Validate the row
      if (!row.first_name) {
        row.errors.push('First name is required');
        row.isValid = false;
      }
      if (!row.last_name) {
        row.errors.push('Last name is required');
        row.isValid = false;
      }
      if (!row.email) {
        row.errors.push('Email is required');
        row.isValid = false;
      } else if (!validateEmail(row.email)) {
        row.errors.push('Invalid email format');
        row.isValid = false;
      }
      if (row.cell_phone && !validatePhoneNumber(row.cell_phone)) {
        row.errors.push('Invalid phone number format');
        row.isValid = false;
      }

      // Skip completely empty rows
      if (!row.first_name && !row.last_name && !row.email && !row.cell_phone) {
        continue;
      }

      data.push(row);
    }

    return data;
  };

  const checkExistingProfiles = async (data: CSVRow[]): Promise<CSVRow[]> => {
    const emails = data.filter(row => row.email).map(row => row.email.toLowerCase());
    
    if (emails.length === 0) return data;

    try {
      const { data: existingProfiles, error } = await supabase
        .from('profiles')
        .select('id, email, name')
        .in('email', emails);

      if (error) throw error;

      const profileMap = new Map(existingProfiles.map(p => [p.email.toLowerCase(), p]));

      return data.map(row => ({
        ...row,
        existingProfile: profileMap.has(row.email.toLowerCase()) 
          ? profileMap.get(row.email.toLowerCase())?.name || 'Existing User'
          : undefined
      }));
    } catch (error) {
      console.error('Error checking existing profiles:', error);
      return data;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsedData = parseCSV(csvText);
        
        if (parsedData.length === 0) return;

        const dataWithProfiles = await checkExistingProfiles(parsedData);
        setCsvData(dataWithProfiles);
        setShowPreview(true);

        const validCount = dataWithProfiles.filter(row => row.isValid).length;
        const invalidCount = dataWithProfiles.length - validCount;
        
        toast.success(`Parsed ${dataWithProfiles.length} rows. ${validCount} valid, ${invalidCount} invalid.`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Failed to parse CSV file');
      }
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    const validRows = csvData.filter(row => row.isValid);
    if (validRows.length === 0) {
      toast.error('No valid rows to import');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Get current user info for invitations
      const currentUser = (await getSessionUser()).data.user;
      const { data: coachProfile } = await supabase
        .from('profiles')
        .select('name')
        .eq('user_id', currentUser?.id)
        .single();

      // Get organization_id from organization_members (RLevents schema)
      const { data: userRole } = await (supabase as any)
        .from('organization_members')
        .select('organization_id, role')
        .eq('user_id', user?.id || currentUser?.id)
        .eq('status', 'active')
        .in('role', ['org_admin', 'org_staff'])
        .maybeSingle();

      for (const row of validRows) {
        try {
          // Check if email matches existing athlete
          let profileId = null;
          if (row.email) {
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('id')
              .eq('email', row.email.toLowerCase())
              .maybeSingle();

            if (existingProfile) {
              profileId = existingProfile.id;
            }
          }

          // Insert roster member
          const rosterData = {
            team_id: teamId,
            first_name: row.first_name.trim(),
            last_name: row.last_name.trim(),
            email: row.email.toLowerCase(),
            cell_phone: row.cell_phone || null,
            profile_id: profileId,
            invite_sent: false,
            organization_id: userRole?.organization_id || null
          };

          const { data: insertedRoster, error } = await supabase
            .from('coach_team_rosters')
            .insert(rosterData)
            .select()
            .single();

          if (error) throw error;

          // Send invitation if email is provided
          if (row.email) {
            try {
              await sendRosterInvitation(
                teamId,
                insertedRoster.id,
                {
                  first_name: row.first_name.trim(),
                  last_name: row.last_name.trim(),
                  email: row.email.toLowerCase(),
                  cell_phone: row.cell_phone || undefined
                },
                teamName,
                coachProfile?.name || 'Coach',
                currentUser?.id || ''
              );
            } catch (inviteError) {
              console.error('Invitation error for', row.email, inviteError);
              // Don't fail the whole import for invitation errors
            }
          }

          successCount++;
        } catch (error) {
          console.error('Error importing row:', row, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} athletes`);
        onImportComplete();
        setShowPreview(false);
        setCsvData([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} athletes`);
      }

    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import roster');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Import Roster from CSV
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showPreview ? (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              Upload a CSV file with your roster information. The file should include first_name, last_name, email, and optionally cell_phone columns.
            </div>
            
            <div className="flex gap-2">
              <Button onClick={generateTemplate} variant="outline" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download Template
              </Button>
              
              <div className="flex-1">
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium"
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>first_name</strong> (required): Athlete's first name</li>
                <li>• <strong>last_name</strong> (required): Athlete's last name</li>
                <li>• <strong>email</strong> (required): Valid email address for invitations</li>
                <li>• <strong>cell_phone</strong> (optional): Phone number for SMS notifications</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Import Preview</h3>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setShowPreview(false)} 
                  variant="outline"
                  disabled={isUploading}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={isUploading || csvData.filter(row => row.isValid).length === 0}
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? 'Importing...' : `Import ${csvData.filter(row => row.isValid).length} Athletes`}
                </Button>
              </div>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {csvData.map((row, index) => (
                    <TableRow key={index} className={!row.isValid ? 'bg-red-50' : undefined}>
                      <TableCell>
                        {row.isValid ? (
                          <Badge className="bg-green-100 text-green-800">
                            <Check className="h-3 w-3 mr-1" />
                            Valid
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Invalid
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{row.first_name} {row.last_name}</div>
                          {row.existingProfile && (
                            <div className="text-sm text-blue-600">Existing: {row.existingProfile}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.cell_phone || '-'}</TableCell>
                      <TableCell>
                        {row.errors.length > 0 && (
                          <div className="text-sm text-red-600">
                            {row.errors.join(', ')}
                          </div>
                        )}
                        {row.isValid && row.existingProfile && (
                          <div className="text-sm text-blue-600">
                            Will link to existing profile
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Total rows: {csvData.length}</span>
              <span>Valid: {csvData.filter(row => row.isValid).length} | Invalid: {csvData.filter(row => !row.isValid).length}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RosterCSVImport;